import { App } from '@capacitor/app';
import { isNative } from '@/lib/platform';
import { useUIStore } from '@/stores/uiStore';

/**
 * Universal Links router — https://brainday.app/e/<target> (the stable CTA
 * URLs baked into the launch emails and ads; see
 * docs/marketing/launch/deep-links-setup.md). Any other brainday.app URL is
 * ignored on purpose: the Firebase auto-AASA claims /* for the app, so
 * non-/e/ links open the app to whatever screen it was on.
 */

/** Actions only AppShell can provide — the capture composer is AppShell-local
 *  state and the day builder is a hook, so neither is reachable from module
 *  scope the way uiStore is. */
export interface DeepLinkActions {
  navigate: (path: string) => void | Promise<void>;
  openCapture: () => void;
  runBuild: () => void;
}

function scrollToSection(id: string, attempt = 0): void {
  const el = document.getElementById(id);
  if (el) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    return;
  }
  // The Settings page is a lazy route — poll briefly until its chunk mounts.
  if (attempt >= 10) return;
  setTimeout(() => scrollToSection(id, attempt + 1), 200);
}

function routeForTarget(
  target: string,
  actions: DeepLinkActions,
): { path: string; action?: () => void } {
  switch (target) {
    case 'today':
      return { path: '/app' };
    case 'build':
      return { path: '/app', action: actions.runBuild };
    case 'capture':
      return { path: '/app', action: actions.openCapture };
    case 'ask':
      // Opens CommandPalette on md+ and AskSheet below md — both ride this flag.
      return { path: '/app', action: () => useUIStore.getState().setCommandPaletteOpen(true) };
    case 'habits':
      return { path: '/app/habits' };
    case 'rhythm':
      return { path: '/app/settings', action: () => scrollToSection('day-rhythm') };
    case 'settings':
      return { path: '/app/settings' };
    case 'pro':
      return {
        path: '/app/settings',
        action: () => useUIStore.getState().setUpgradeSheetRequested(true),
      };
    default:
      return { path: '/app' };
  }
}

// iOS can deliver a cold-start link twice (App.getLaunchUrl AND a late
// appUrlOpen event). Dedupe identical URLs inside a short window so actions
// with real side effects (runBuild spends AI quota) fire once.
const DEDUPE_WINDOW_MS = 1500;
let lastUrl: string | null = null;
let lastUrlAt = 0;

export function handleDeepLink(url: string, actions: DeepLinkActions): void {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return; // custom-scheme URLs (e.g. the Google OAuth redirect) — not ours
  }
  if (parsed.hostname !== 'brainday.app') return;
  const target = /^\/e\/([a-z-]+)/i.exec(parsed.pathname)?.[1];
  if (!target) return;

  const now = Date.now();
  if (url === lastUrl && now - lastUrlAt < DEDUPE_WINDOW_MS) return;
  lastUrl = url;
  lastUrlAt = now;

  const { path, action } = routeForTarget(target.toLowerCase(), actions);
  void actions.navigate(path);
  action?.();
}

// Consumed once per JS session so an AppShell remount doesn't replay the
// launch URL as a fresh navigation.
let launchUrlConsumed = false;

/** Call once from a component inside the Router that is always mounted
 *  (AppShell), after stores have hydrated. Returns a cleanup function. */
export function initDeepLinks(actions: DeepLinkActions): () => void {
  if (!isNative()) return () => undefined;

  // Cold start: the app was launched by tapping the link.
  if (!launchUrlConsumed) {
    launchUrlConsumed = true;
    void App.getLaunchUrl().then((result) => {
      if (result?.url) handleDeepLink(result.url, actions);
    });
  }

  // Warm start: link tapped while the app is running.
  const listener = App.addListener('appUrlOpen', (event) => handleDeepLink(event.url, actions));
  return () => {
    void listener.then((handle) => handle.remove());
  };
}
